import React, { useMemo, useRef } from 'react';
import { Container, Form, Table } from 'react-bootstrap';
import { getComparator, stableSort } from '../../lib/utils'; // Import sorting utilities if needed
import PaginationComponent from './PaginationComponent';
import { Link } from 'react-router-dom';
import { North, South } from '@mui/icons-material';
import TableLoader from './TableLoader';

const defaultIsRowDisabled = () => false;

const DraggableTableComponent = ({
  data,
  headerTabs,
  page,
  perPage,
  showCheckbox,
  action,
  defaultSort,
  order,
  setOrder,
  selected,
  setSelected,
  showPages,
  striped,
  isLoading,
  setSelectedRows = () => {},
  totalCount,
  handlePageChange,
  tablekey,
  isRowDisabled = defaultIsRowDisabled,
  checkboxDisabledTitle,
  updateSequence
}) => {
  const [orderBy, setOrderBy] = React.useState(defaultSort || '');
  const containerRef = useRef(null);
  const isSelected = (name) => selected && selected.indexOf(name) !== -1;
  const handleClick = (event, name, row) => {
    if (isRowDisabled(row)) return; // Ignore click if row is disabled

    const selectedIndex = selected?.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
    const selectedRowsData = data.filter((row) => newSelected.includes(row.id));
    setSelectedRows(selectedRowsData);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allSelected = data?.filter(row => !isRowDisabled(row) && !row.isRowDisabled).map((r) => r.id);
      setSelected(allSelected);
      setSelectedRows(data.filter(row => !isRowDisabled(row) && !row.isRowDisabled));
    } else {
      setSelected([]);
      setSelectedRows([]);
    }
  };

  const handleDragStart = (e, index, row) => {
    e.dataTransfer.setData("draggedIndex", index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  
    const SCROLL_THRESHOLD = 50; // Distance from the edge of the container
    const SCROLL_SPEED = 10; // Pixels to scroll per frame
  
    const { clientY } = e; // Mouse position
    const windowHeight = window.innerHeight;
  
    const container = containerRef.current;
    if (!container) return;
  
    const containerRect = container.getBoundingClientRect();
    const containerTop = containerRect.top;
    const containerBottom = containerRect.bottom;
  
    // Check if the mouse is near the top or bottom of the container
    const isNearContainerTop = clientY < containerTop + SCROLL_THRESHOLD;
    const isNearContainerBottom = clientY > containerBottom - SCROLL_THRESHOLD;
  
    // Detect if the container is scrollable
    const canScrollUp = container.scrollTop > 0;
    const canScrollDown =
      container.scrollTop + container.clientHeight < container.scrollHeight;
  
    // Scroll inside the container first
    if (isNearContainerTop && canScrollUp) {
      container.scrollTop -= SCROLL_SPEED;
    } else if (isNearContainerBottom && canScrollDown) {
      container.scrollTop += SCROLL_SPEED;
    }
  
    // If the container can't scroll further, scroll the window
    if (
      (isNearContainerBottom && !canScrollDown) || // Container can't scroll further
      (containerBottom > windowHeight && clientY > windowHeight - SCROLL_THRESHOLD) // Mouse is near the hidden bottom
    ) {
      window.scrollBy(0, SCROLL_SPEED);
    }
    if (
      (isNearContainerTop && !canScrollUp) || // Container can't scroll further
      (containerTop < 0 && clientY < SCROLL_THRESHOLD) // Mouse is near the hidden top of the table
    ) {
      window.scrollBy(0, -SCROLL_SPEED); // Scroll the window upwards
    }
  
    // For scrolling down when the bottom of the table is out of view
    if (isNearContainerBottom && !canScrollDown) {
      // If the container can't scroll further, scroll the window down
      window.scrollBy(0, SCROLL_SPEED);
    }
  };
  
  
  
  
  const finalData = useMemo(() => {
    return stableSort(data, getComparator(order, orderBy))
  }, [data, orderBy, order])

  const handleDrop = async  (e, dropIndex, row) => {
    const draggedIndex = e.dataTransfer.getData("draggedIndex");
     const updatedRows = [...finalData];
    const [draggedRow] = updatedRows.splice(draggedIndex, 1);
    updatedRows.splice(dropIndex, 0, draggedRow);
    const rowsWithUpdatedSrNo = updatedRows.map((row, index) => ({
        ...row,
        sNo: index + 1,
      }));
    await updateSequence(rowsWithUpdatedSrNo)
  };

  const handleSort = (key) => {
    setOrder(order === 'asc' ? 'desc' : 'asc');
    setOrderBy(key);
  };
  return (
    <Container fluid className="px-0 table-container drag-table" ref={containerRef}  >
      <Table striped={striped} responsive className='border' style={{ tableLayout: 'fixed', width: '100%' }}>
        <thead>
          <tr>
            {showCheckbox && !isLoading && (
              <th className="text-center">
                <Form.Check
                  type="checkbox"
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
            )}
            {headerTabs &&
              headerTabs?.map((tab) => (
                <th
                  title={tab.hoverText || tab.label}
                  className={tab?.sortable ? 'cursor-pointer' : 'cursor-arrow'}
                  style={{
                    width: tab?.width || 'auto',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                  }}
                  key={tab.key}
                  onClick={tab?.sortable ? () => handleSort(tab.key) : () => { }}>
                  {tab.label}{' '}
                  {tab?.sortable && tab.key === orderBy ? order === 'asc' ? <North /> : <South /> : null}
                </th>
              ))}
            {action && <th></th>}
          </tr>
        </thead>
        {isLoading ? (
          <TableLoader
            colSpan={headerTabs.length + 1}
          />
        ) : (
          <tbody onDragOver={handleDragOver}>
            {data?.length === 0 && (
              <tr>
                <td colSpan={headerTabs.length + 1} className="text-center">
                  No {tablekey || 'data'} available
                </td>
              </tr>
            )}
            {finalData
              .map((row, rowIndex) => {
                const isItemSelected = isSelected(row.id);
                const disabled = isRowDisabled(row); // Check if row is disabled
                return (
                  <tr
                    draggable
                    onDragStart={(e) => handleDragStart(e, rowIndex, row)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, rowIndex, row)}
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={'row' + (rowIndex + 1)}
                    selected={isItemSelected}
                    title={row.isRowDisabled ? checkboxDisabledTitle : disabled ? 'Already added' : '' }
                    className={disabled ? 'table-secondary' : ''}
                  >
                    {showCheckbox && (
                      <td className="text-center">
                        <Form.Check
                          type="checkbox"
                          checked={isItemSelected || disabled}
                          onChange={(event) => handleClick(event, row.id, row)}
                          disabled={disabled || row.isRowDisabled}
                          className={disabled ? 'checkbox-disabled' : ''}
                        />
                      </td>
                    )}
                    {headerTabs &&
                      headerTabs.map(
                        (
                          {
                            key,
                            onClick,
                            paramToPass,
                            linkToId,
                            formatContent,
                          },
                          index
                        ) => {
                          return (
                            <td key={'col' + index + rowIndex} align="left"  style={{
                              width: headerTabs[index]?.width || 'auto',
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                            }}>
                              {formatContent ? (
                                formatContent(row)
                              ) : linkToId ? (
                                <Link to={`/${linkToId}/${row.id}`}>
                                  {row[key]}
                                </Link>
                              ) : (
                                row[key] || '-'
                              )}
                            </td>
                          );
                        }
                      )}
                    {action && (
                      <td className="row-action" align="left">
                        {action(row)}
                      </td>
                    )}
                  </tr>
                );
              })}
          </tbody>
        )}
      </Table>
      {showPages && (
        <PaginationComponent
          totalCount={totalCount}
          currentPage={page}
          handlePageChange={handlePageChange}
          perPage={perPage}
        />
      )}
    </Container>
  );
};

export default DraggableTableComponent;
